import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { api, del, get, patch, post } from "./client";
import type {
  CalEvent,
  Calendar,
  CycleData,
  CycleDay,
  CycleSettings,
  EventInput,
  FinanceBudget,
  FinanceEntry,
  FinanceGoal,
  FinanceMonth,
  HealthDemand,
  HealthDemands,
  HealthRoutine,
  Holiday,
  Me,
  Resolution,
  Resolutions,
  Scope,
  Settings,
  TaskHistory,
  Tasks,
  UsagePeriod,
  UsageSummary,
  UsedCategory,
  UserUsageDetail
} from "./types";
import type { DateStr } from "../lib/date";

export const keys = {
  me: ["me"] as const,
  calendars: ["calendars"] as const,
  events: (from: DateStr, to: DateStr) => ["events", from, to] as const,
  search: (q: string) => ["search", q] as const,
  tasks: ["tasks"] as const,
  taskHistory: ["tasks", "history"] as const,
  categories: ["categories"] as const,
  resolutions: ["resolutions"] as const,
  healthDemands: ["health", "demands"] as const,
  healthRoutines: ["health", "routines"] as const,
  financeMonth: (month: string) => ["finance", "month", month] as const,
  financeGoals: ["finance", "goals"] as const,
  financeBudgets: ["finance", "budgets"] as const,
  cycle: (from: DateStr, to: DateStr) => ["cycle", from, to] as const
};

export function useMe() {
  return useQuery({ queryKey: keys.me, queryFn: () => get<Me>("/api/v1/me"), retry: false, staleTime: 60_000 });
}

export function useCalendars() {
  return useQuery({ queryKey: keys.calendars, queryFn: () => get<Calendar[]>("/api/v1/calendars"), staleTime: 60_000 });
}

export interface EventsResponse {
  events: CalEvent[];
  holidays: Holiday[];
}

export function useEvents(from: DateStr, to: DateStr, enabled = true) {
  return useQuery({
    queryKey: keys.events(from, to),
    queryFn: () => get<EventsResponse>(`/api/v1/events?from=${from}&to=${to}`),
    enabled,
    placeholderData: (prev) => prev,
    staleTime: 30_000
  });
}

export function prefetchEvents(qc: QueryClient, from: DateStr, to: DateStr) {
  return qc.prefetchQuery({ queryKey: keys.events(from, to), queryFn: () => get<EventsResponse>(`/api/v1/events?from=${from}&to=${to}`), staleTime: 30_000 });
}

export function useSearch(q: string) {
  return useQuery({
    queryKey: keys.search(q),
    queryFn: () => get<{ events: CalEvent[] }>(`/api/v1/events/search?q=${encodeURIComponent(q)}`),
    enabled: q.trim().length > 1
  });
}

/** Refetch whatever changed when the server says so (WhatsApp edits, other tabs). */
export function useLiveUpdates(enabled: boolean) {
  const qc = useQueryClient();
  useEffect(() => {
    if (!enabled || typeof EventSource === "undefined") return;
    const source = new EventSource("/api/v1/stream");
    source.addEventListener("change", (e) => {
      const topic = JSON.parse((e as MessageEvent).data).topic as string;
      if (topic === "events") {
        invalidateEvents(qc);
      } else if (topic === "resolutions") {
        qc.invalidateQueries({ queryKey: keys.resolutions });
      } else if (topic === "health" || topic === "finance" || topic === "cycle") {
        qc.invalidateQueries({ queryKey: [topic] });
      } else if (topic === "calendars") {
        qc.invalidateQueries({ queryKey: keys.calendars });
        qc.invalidateQueries({ queryKey: ["events"] });
      } else {
        qc.invalidateQueries({ queryKey: keys.me });
      }
    });
    return () => source.close();
  }, [enabled, qc]);
}

function invalidateEvents(qc: QueryClient) {
  qc.invalidateQueries({ queryKey: ["events"] });
  qc.invalidateQueries({ queryKey: ["search"] });
  qc.invalidateQueries({ queryKey: keys.tasks });
  qc.invalidateQueries({ queryKey: keys.categories });
}

export function useTasks() {
  return useQuery({ queryKey: keys.tasks, queryFn: () => get<Tasks>("/api/v1/tasks"), staleTime: 30_000 });
}

export function useTaskHistory() {
  return useQuery({ queryKey: keys.taskHistory, queryFn: () => get<TaskHistory>("/api/v1/tasks/history"), staleTime: 30_000 });
}

export function useCategories() {
  return useQuery({ queryKey: keys.categories, queryFn: () => get<UsedCategory[]>("/api/v1/categories"), staleTime: 60_000 });
}

/** Marks a task (or one occurrence of a routine) done / pending again (optimistic: it moves between the lists at once). */
export function useCompleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ event, completed }: { event: CalEvent; completed: boolean }) =>
      post<CalEvent>(`/api/v1/events/${event.eventId}/complete`, {
        completed,
        // An occurrence of a routine is checked off on its own.
        occurrenceStart: event.seriesId === event.eventId ? event.occurrenceStart ?? undefined : undefined
      }),
    onMutate: async ({ event, completed }) => {
      await qc.cancelQueries({ queryKey: keys.tasks });
      const prev = qc.getQueryData<Tasks>(keys.tasks);
      if (prev) {
        const moved = { ...event, completed };
        const pending = prev.pending.filter((e) => e.key !== event.key);
        const done = prev.completed.filter((e) => e.key !== event.key);
        qc.setQueryData<Tasks>(keys.tasks, {
          pending: completed ? pending : [...pending, moved].sort((a, b) => a.start.localeCompare(b.start)),
          completed: completed ? [moved, ...done] : done
        });
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => ctx?.prev && qc.setQueryData(keys.tasks, ctx.prev),
    onSettled: () => invalidateEvents(qc)
  });
}

export function useCreateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: EventInput) => post<CalEvent>("/api/v1/events", input),
    onSettled: () => invalidateEvents(qc)
  });
}

export interface UpdateArgs {
  event: CalEvent;
  scope: Scope;
  changes: Partial<EventInput>;
}

/** Optimistic for simple time moves of a single occurrence (drag & drop, resize). */
export function useUpdateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ event, scope, changes }: UpdateArgs) =>
      patch<CalEvent>(`/api/v1/events/${event.eventId}`, { scope, occurrenceStart: event.occurrenceStart ?? undefined, changes }),
    onMutate: async ({ event, scope, changes }) => {
      if (scope !== "this" && event.isRecurring) return;
      await qc.cancelQueries({ queryKey: ["events"] });
      const snapshots = qc.getQueriesData<EventsResponse>({ queryKey: ["events"] });
      for (const [key, data] of snapshots) {
        if (!data) continue;
        qc.setQueryData<EventsResponse>(key, {
          ...data,
          events: data.events.map((e) => (e.key === event.key ? { ...e, ...(changes as Partial<CalEvent>) } : e))
        });
      }
      return { snapshots };
    },
    onError: (_err, _vars, ctx) => {
      for (const [key, data] of ctx?.snapshots ?? []) qc.setQueryData(key, data);
    },
    onSettled: () => invalidateEvents(qc)
  });
}

export interface DeleteArgs {
  event: CalEvent;
  scope: Scope;
}

export function useDeleteEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ event, scope }: DeleteArgs) => {
      const params = new URLSearchParams({ scope });
      if (event.occurrenceStart && scope !== "all") params.set("occurrenceStart", event.occurrenceStart);
      return del<{ undo: { eventId: string; occurrenceStart?: string } | null }>(`/api/v1/events/${event.eventId}?${params}`);
    },
    onMutate: async ({ event, scope }) => {
      await qc.cancelQueries({ queryKey: ["events"] });
      const snapshots = qc.getQueriesData<EventsResponse>({ queryKey: ["events"] });
      for (const [key, data] of snapshots) {
        if (!data) continue;
        const series = event.seriesId ?? event.eventId;
        qc.setQueryData<EventsResponse>(key, {
          ...data,
          events: data.events.filter((e) =>
            scope === "this" || !event.isRecurring
              ? e.key !== event.key
              : scope === "all"
                ? (e.seriesId ?? e.eventId) !== series
                : !((e.seriesId ?? e.eventId) === series && e.start >= event.start)
          )
        });
      }
      return { snapshots };
    },
    onError: (_err, _vars, ctx) => {
      for (const [key, data] of ctx?.snapshots ?? []) qc.setQueryData(key, data);
    },
    onSettled: () => invalidateEvents(qc)
  });
}

export function useRestoreEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (undo: { eventId: string; occurrenceStart?: string }) =>
      post<CalEvent>(`/api/v1/events/${undo.eventId}/restore`, { occurrenceStart: undo.occurrenceStart }),
    onSettled: () => invalidateEvents(qc)
  });
}

/**
 * "Virar resolução": the item (a routine as a whole) leaves the agenda and becomes an
 * undated resolution. `undo` brings it back and drops the resolution.
 */
export function useEventToResolution() {
  const qc = useQueryClient();
  const settled = () => {
    invalidateEvents(qc);
    qc.invalidateQueries({ queryKey: keys.resolutions });
  };
  const convert = useMutation({
    mutationFn: (event: CalEvent) => post<{ resolution: Resolution; undo: { eventId: string } | null }>(`/api/v1/events/${event.eventId}/to-resolution`),
    onMutate: async (event) => {
      await qc.cancelQueries({ queryKey: ["events"] });
      const snapshots = qc.getQueriesData<EventsResponse>({ queryKey: ["events"] });
      const series = event.seriesId ?? event.eventId;
      for (const [key, data] of snapshots) {
        if (data) qc.setQueryData<EventsResponse>(key, { ...data, events: data.events.filter((e) => (e.seriesId ?? e.eventId) !== series) });
      }
      return { snapshots };
    },
    onError: (_e, _v, ctx) => ctx?.snapshots.forEach(([key, data]) => qc.setQueryData(key, data)),
    onSettled: settled
  });
  const undo = useMutation({
    mutationFn: async ({ eventId, resolutionId }: { eventId: string; resolutionId: string }) => {
      await post(`/api/v1/events/${eventId}/restore`, {});
      await del(`/api/v1/resolutions/${resolutionId}`);
    },
    onSettled: settled
  });
  return { convert, undo };
}

export function useCalendarMutations() {
  const qc = useQueryClient();
  const done = () => {
    qc.invalidateQueries({ queryKey: keys.calendars });
    invalidateEvents(qc);
  };
  return {
    create: useMutation({ mutationFn: (b: { name: string; color: string }) => post<Calendar>("/api/v1/calendars", b), onSettled: done }),
    update: useMutation({
      mutationFn: ({ id, ...b }: { id: string; name?: string; color?: string; isVisible?: boolean }) => patch<Calendar>(`/api/v1/calendars/${id}`, b),
      onMutate: async ({ id, ...b }) => {
        await qc.cancelQueries({ queryKey: keys.calendars });
        const prev = qc.getQueryData<Calendar[]>(keys.calendars);
        qc.setQueryData<Calendar[]>(keys.calendars, (list) => list?.map((c) => (c.id === id ? { ...c, ...b } : c)));
        return { prev };
      },
      onError: (_e, _v, ctx) => qc.setQueryData(keys.calendars, ctx?.prev),
      onSettled: done
    }),
    remove: useMutation({ mutationFn: (id: string) => del(`/api/v1/calendars/${id}`), onSettled: done })
  };
}

export function useUpdateSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (changes: Partial<Settings> & { timezone?: string }) => patch<Me>("/api/v1/settings", changes),
    onMutate: async (changes) => {
      const prev = qc.getQueryData<Me>(keys.me);
      if (prev) {
        const { timezone, ...settings } = changes;
        qc.setQueryData<Me>(keys.me, { ...prev, timezone: timezone ?? prev.timezone, settings: { ...prev.settings, ...settings } });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => ctx?.prev && qc.setQueryData(keys.me, ctx.prev),
    onSuccess: (me) => {
      qc.setQueryData(keys.me, me);
      invalidateEvents(qc);
    }
  });
}

export function useResolutions() {
  return useQuery({ queryKey: keys.resolutions, queryFn: () => get<Resolutions>("/api/v1/resolutions"), staleTime: 30_000 });
}

/** Create / check / rename / delete, all optimistic on the cached lists. */
export function useResolutionMutations() {
  const qc = useQueryClient();
  const optimistic = (apply: (data: Resolutions) => Resolutions) => async () => {
    await qc.cancelQueries({ queryKey: keys.resolutions });
    const prev = qc.getQueryData<Resolutions>(keys.resolutions);
    if (prev) qc.setQueryData(keys.resolutions, apply(prev));
    return { prev };
  };
  const rollback = (_e: unknown, _v: unknown, ctx?: { prev?: Resolutions }) => ctx?.prev && qc.setQueryData(keys.resolutions, ctx.prev);
  const settled = () => qc.invalidateQueries({ queryKey: keys.resolutions });
  const without = (list: Resolution[], id: string) => list.filter((r) => r.id !== id);
  return {
    create: useMutation({
      mutationFn: (title: string) => post<Resolution>("/api/v1/resolutions", { title }),
      onMutate: (title) =>
        optimistic((d) => ({
          ...d,
          pending: [...d.pending, { id: `tmp-${Date.now()}`, title, completed: false, completedAt: null, createdAt: new Date().toISOString() }]
        }))(),
      onError: rollback,
      onSettled: settled
    }),
    update: useMutation({
      mutationFn: ({ r, ...changes }: { r: Resolution; title?: string; completed?: boolean }) => patch<Resolution>(`/api/v1/resolutions/${r.id}`, changes),
      onMutate: ({ r, ...changes }) =>
        optimistic((d) => {
          const next = { ...r, ...changes };
          if (changes.completed === undefined) {
            const swap = (list: Resolution[]) => list.map((x) => (x.id === r.id ? next : x));
            return { pending: swap(d.pending), completed: swap(d.completed) };
          }
          const pending = without(d.pending, r.id);
          const completed = without(d.completed, r.id);
          return changes.completed
            ? { pending, completed: [{ ...next, completedAt: new Date().toISOString() }, ...completed] }
            : { pending: [...pending, { ...next, completedAt: null }].sort((a, b) => a.createdAt.localeCompare(b.createdAt)), completed };
        })(),
      onError: rollback,
      onSettled: settled
    }),
    remove: useMutation({
      mutationFn: (r: Resolution) => del(`/api/v1/resolutions/${r.id}`),
      onMutate: (r) => optimistic((d) => ({ pending: without(d.pending, r.id), completed: without(d.completed, r.id) }))(),
      onError: rollback,
      onSettled: settled
    })
  };
}

export function useAdminUsage(period: UsagePeriod) {
  return useQuery({
    queryKey: ["admin", "usage", period],
    queryFn: () => get<UsageSummary>(`/api/v1/admin/usage?period=${period}`),
    staleTime: 30_000,
    placeholderData: (prev) => prev
  });
}

/** Unlocking a user paused by the hourly cap, and blocking / unblocking one by hand. */
export function useAdminAccess() {
  const qc = useQueryClient();
  const settled = () => qc.invalidateQueries({ queryKey: ["admin", "usage"] });
  return {
    reset: useMutation({
      mutationFn: (userId: string) => post(`/api/v1/admin/users/${userId}/rate-limit/reset`),
      onSettled: settled
    }),
    block: useMutation({
      mutationFn: ({ userId, blocked }: { userId: string; blocked: boolean }) => api("PUT", `/api/v1/admin/users/${userId}/blocked`, { blocked }),
      onSettled: settled
    })
  };
}

export function useAdminUserUsage(userId: string, period: UsagePeriod) {
  return useQuery({
    queryKey: ["admin", "usage", period, userId],
    queryFn: () => get<UserUsageDetail>(`/api/v1/admin/usage/users/${userId}?period=${period}`),
    staleTime: 30_000
  });
}

// --- Saúde / Finanças / Ciclo -------------------------------------------------------------

/**
 * A mutation that patches one cached query at once (`apply`), rolls it back on error and
 * refetches everything under `invalidate` when it settles.
 */
function useCachedMutation<D, V, R>(opts: {
  queryKey: readonly unknown[];
  invalidate: readonly unknown[];
  mutationFn: (v: V) => Promise<R>;
  apply?: (data: D, v: V) => D;
}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: opts.mutationFn,
    onMutate: async (v: V) => {
      if (!opts.apply) return { prev: undefined };
      await qc.cancelQueries({ queryKey: opts.queryKey });
      const prev = qc.getQueryData<D>(opts.queryKey);
      if (prev) qc.setQueryData(opts.queryKey, opts.apply(prev, v));
      return { prev };
    },
    onError: (_e, _v, ctx) => ctx?.prev && qc.setQueryData(opts.queryKey, ctx.prev),
    onSettled: () => qc.invalidateQueries({ queryKey: opts.invalidate })
  });
}

export function useHealthDemands() {
  return useQuery({ queryKey: keys.healthDemands, queryFn: () => get<HealthDemands>("/api/v1/health/demands"), staleTime: 30_000 });
}

export function useHealthDemandMutations() {
  const base = { queryKey: keys.healthDemands, invalidate: keys.healthDemands };
  const without = (list: HealthDemand[], id: string) => list.filter((x) => x.id !== id);
  return {
    create: useCachedMutation<HealthDemands, DemandInput, HealthDemand>({
      ...base,
      mutationFn: (b) => post<HealthDemand>("/api/v1/health/demands", b),
      apply: (d, b) => ({
        ...d,
        pending: [
          ...d.pending,
          { id: `tmp-${Date.now()}`, dueDate: null, dueTime: null, ...b, onAgenda: Boolean(b.dueDate), completed: false, completedAt: null, createdAt: new Date().toISOString() }
        ]
      })
    }),
    update: useCachedMutation<HealthDemands, { item: HealthDemand } & Partial<Pick<HealthDemand, "title" | "origin" | "completed" | "dueDate" | "dueTime">>, HealthDemand>({
      ...base,
      mutationFn: ({ item, ...changes }) => patch<HealthDemand>(`/api/v1/health/demands/${item.id}`, changes),
      apply: (d, { item, ...changes }) => {
        const next = { ...item, ...changes };
        if (changes.completed === undefined) {
          const swap = (list: HealthDemand[]) => list.map((x) => (x.id === item.id ? next : x));
          return { pending: swap(d.pending), completed: swap(d.completed) };
        }
        const pending = without(d.pending, item.id);
        const completed = without(d.completed, item.id);
        return changes.completed
          ? { pending, completed: [{ ...next, completedAt: new Date().toISOString() }, ...completed] }
          : { pending: [...pending, { ...next, completedAt: null }].sort((a, b) => a.createdAt.localeCompare(b.createdAt)), completed };
      }
    }),
    remove: useCachedMutation<HealthDemands, HealthDemand, unknown>({
      ...base,
      mutationFn: (item) => del(`/api/v1/health/demands/${item.id}`),
      apply: (d, item) => ({ pending: without(d.pending, item.id), completed: without(d.completed, item.id) })
    })
  };
}

export type DemandInput = Pick<HealthDemand, "title" | "origin" | "kind"> & Partial<Pick<HealthDemand, "dueDate" | "dueTime">>;

export function useHealthRoutines() {
  return useQuery({ queryKey: keys.healthRoutines, queryFn: () => get<HealthRoutine[]>("/api/v1/health/routines"), staleTime: 30_000 });
}

export type RoutineInput = Pick<HealthRoutine, "title" | "person" | "lastDone" | "intervalMonths" | "note">;

export function useHealthRoutineMutations() {
  const base = { queryKey: keys.healthRoutines, invalidate: keys.healthRoutines };
  return {
    create: useCachedMutation<HealthRoutine[], RoutineInput, HealthRoutine>({
      ...base,
      mutationFn: (b) => post<HealthRoutine>("/api/v1/health/routines", b),
      apply: (list, b) => [...list, { id: `tmp-${Date.now()}`, ...b, onAgenda: Boolean(b.lastDone), createdAt: new Date().toISOString() }]
    }),
    update: useCachedMutation<HealthRoutine[], { item: HealthRoutine } & Partial<RoutineInput>, HealthRoutine>({
      ...base,
      mutationFn: ({ item, ...changes }) => patch<HealthRoutine>(`/api/v1/health/routines/${item.id}`, changes),
      apply: (list, { item, ...changes }) => list.map((x) => (x.id === item.id ? { ...x, ...changes } : x))
    }),
    remove: useCachedMutation<HealthRoutine[], HealthRoutine, unknown>({
      ...base,
      mutationFn: (item) => del(`/api/v1/health/routines/${item.id}`),
      apply: (list, item) => list.filter((x) => x.id !== item.id)
    })
  };
}

export function useFinanceMonth(month: string) {
  return useQuery({
    queryKey: keys.financeMonth(month),
    queryFn: () => get<FinanceMonth>(`/api/v1/finance/months/${month}`),
    placeholderData: (prev) => prev,
    staleTime: 30_000
  });
}

export type EntryInput = Pick<FinanceEntry, "month" | "kind" | "title" | "amountCents" | "dueDay" | "fixed"> & { category?: string };
export type EntryChanges = Partial<Pick<FinanceEntry, "title" | "amountCents" | "dueDay" | "fixed" | "paid" | "category">>;

/** Same order as the server: by due day (no day last), then oldest first. */
function byDueDay(a: FinanceEntry, b: FinanceEntry): number {
  return (a.dueDay ?? 99) - (b.dueDay ?? 99) || a.createdAt.localeCompare(b.createdAt);
}

export function useFinanceEntryMutations(month: string) {
  const qc = useQueryClient();
  const base = { queryKey: keys.financeMonth(month), invalidate: ["finance"] as const };
  return {
    create: useCachedMutation<FinanceMonth, EntryInput, FinanceEntry>({
      ...base,
      mutationFn: (b) => post<FinanceEntry>("/api/v1/finance/entries", b),
      apply: (d, b) => ({
        ...d,
        entries: [
          ...d.entries,
          {
            id: `tmp-${Date.now()}`,
            ...b,
            category: b.category ?? (b.kind === "expense" ? "outros" : "outras_entradas"),
            paid: false,
            occurredOn: null,
            onAgenda: false,
            createdAt: new Date().toISOString()
          }
        ].sort(byDueDay)
      })
    }),
    update: useCachedMutation<FinanceMonth, { item: FinanceEntry } & EntryChanges, FinanceEntry>({
      ...base,
      mutationFn: ({ item, ...changes }) => patch<FinanceEntry>(`/api/v1/finance/entries/${item.id}`, changes),
      apply: (d, { item, ...changes }) => ({ ...d, entries: d.entries.map((x) => (x.id === item.id ? { ...x, ...changes } : x)).sort(byDueDay) })
    }),
    remove: useCachedMutation<FinanceMonth, FinanceEntry, unknown>({
      ...base,
      mutationFn: (item) => del(`/api/v1/finance/entries/${item.id}`),
      apply: (d, item) => ({ ...d, entries: d.entries.filter((x) => x.id !== item.id) })
    }),
    copyFixed: useMutation({
      mutationFn: () => post<{ added: number }>(`/api/v1/finance/months/${month}/copy-fixed`),
      onSettled: () => qc.invalidateQueries({ queryKey: ["finance"] })
    })
  };
}

export function useFinanceBudgets() {
  return useQuery({ queryKey: keys.financeBudgets, queryFn: () => get<FinanceBudget[]>("/api/v1/finance/budgets"), staleTime: 30_000 });
}

/** Set (create or change) and remove a category's monthly limit. */
export function useFinanceBudgetMutations() {
  const base = { queryKey: keys.financeBudgets, invalidate: keys.financeBudgets };
  return {
    set: useCachedMutation<FinanceBudget[], FinanceBudget, FinanceBudget>({
      ...base,
      mutationFn: (b) => api<FinanceBudget>("PUT", `/api/v1/finance/budgets/${b.category}`, { limitCents: b.limitCents }),
      apply: (list, b) => (list.some((x) => x.category === b.category) ? list.map((x) => (x.category === b.category ? b : x)) : [...list, b])
    }),
    remove: useCachedMutation<FinanceBudget[], FinanceBudget, unknown>({
      ...base,
      mutationFn: (b) => del(`/api/v1/finance/budgets/${b.category}`),
      apply: (list, b) => list.filter((x) => x.category !== b.category)
    })
  };
}

export function useFinanceGoals() {
  return useQuery({ queryKey: keys.financeGoals, queryFn: () => get<FinanceGoal[]>("/api/v1/finance/goals"), staleTime: 30_000 });
}

export function useFinanceGoalMutations() {
  const base = { queryKey: keys.financeGoals, invalidate: keys.financeGoals };
  return {
    create: useCachedMutation<FinanceGoal[], { title: string; targetCents: number; savedCents: number }, FinanceGoal>({
      ...base,
      mutationFn: (b) => post<FinanceGoal>("/api/v1/finance/goals", b),
      apply: (list, b) => [...list, { id: `tmp-${Date.now()}`, ...b, createdAt: new Date().toISOString() }]
    }),
    update: useCachedMutation<FinanceGoal[], { item: FinanceGoal; title?: string; targetCents?: number; savedCents?: number }, FinanceGoal>({
      ...base,
      mutationFn: ({ item, ...changes }) => patch<FinanceGoal>(`/api/v1/finance/goals/${item.id}`, changes),
      apply: (list, { item, ...changes }) => list.map((x) => (x.id === item.id ? { ...x, ...changes } : x))
    }),
    remove: useCachedMutation<FinanceGoal[], FinanceGoal, unknown>({
      ...base,
      mutationFn: (item) => del(`/api/v1/finance/goals/${item.id}`),
      apply: (list, item) => list.filter((x) => x.id !== item.id)
    })
  };
}

export function useCycle(from: DateStr, to: DateStr) {
  return useQuery({
    queryKey: keys.cycle(from, to),
    queryFn: () => get<CycleData>(`/api/v1/cycle?from=${from}&to=${to}`),
    placeholderData: (prev) => prev,
    staleTime: 30_000
  });
}

export function useCycleMutations(from: DateStr, to: DateStr) {
  const base = { queryKey: keys.cycle(from, to), invalidate: ["cycle"] as const };
  return {
    settings: useCachedMutation<CycleData, Partial<CycleSettings>, CycleSettings>({
      ...base,
      mutationFn: (changes) => patch<CycleSettings>("/api/v1/cycle/settings", changes),
      apply: (d, changes) => ({ ...d, settings: { ...d.settings, ...changes } })
    }),
    day: useCachedMutation<CycleData, { day: DateStr; flow?: boolean; symptoms?: string[] }, CycleDay>({
      ...base,
      mutationFn: ({ day, ...changes }) => api<CycleDay>("PUT", `/api/v1/cycle/days/${day}`, changes),
      apply: (d, { day, ...changes }) => {
        const cur = d.days.find((x) => x.day === day) ?? { day, flow: false, symptoms: [] };
        const next = { ...cur, ...changes };
        const rest = d.days.filter((x) => x.day !== day);
        return { ...d, days: next.flow || next.symptoms.length ? [...rest, next].sort((a, b) => a.day.localeCompare(b.day)) : rest };
      }
    })
  };
}
